/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import _ from 'lodash';
import * as yup from 'yup';
import * as empApi from '../../lib/api/employee';
import dayjs from 'dayjs';
import { useFormik } from 'formik';
import { useSnackbar } from 'notistack';
import { useDispatch, useSelector } from 'react-redux';
import { Box, FormControlLabel, Paper, Switch, Typography } from '@mui/material';
import EmployeeForm from '../../components/employeemng/EmployeeForm';
import { setEmployee } from '../../modules/empModule/employee';
import AskDialog from '../../components/common/dialog/AskDialog';
import { useIsAdmin } from '../../hooks/useIsAdmin';

const isAdminRole = (role) =>
  typeof role === 'string' && role.toUpperCase().includes('ADMIN');

const EmployeeFormContainer = ({ type }) => {
  let navigate = useNavigate();
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();

  // "관리자 권한" 선택칸/전환 스위치 노출 판단 - 관리자만 보인다. 실제 차단은 서버
  // (POST/PUT /user/info, PUT /user/info/{usrUuid}/role)가 한다. 보안 배치 2(권한, 2026-10-27).
  // 오너 결정: KSM은 신규 사원 기본 역할을 관리자로 유지하므로 등록 화면의 기본값은 체크됨(true).
  const isAdmin = useIsAdmin();
  const [newEmployeeIsAdmin, setNewEmployeeIsAdmin] = useState(true);
  const [roleConfirmOpen, setRoleConfirmOpen] = useState(false);

  const { employee } = useSelector(({ empModule }) => ({
    employee: empModule.employee.employee,
  }));
  // 자기 강등 경고(Fable 재검토 항목 5, 2026-10-27) 판단용 - 로그인한 본인의 usrUuid.
  const currentUser = useSelector(({ auth }) => auth.user);

  const initialValues = {
    usrNo: employee && employee.usrNo !== null ? employee.usrNo : '',
    usrNm: employee && employee.usrNm !== null ? employee.usrNm : '',
    password: employee && employee.password !== null ? employee.password : '',
    confirmPassword: '',
    emlAddr: employee && employee.emlAddr !== null ? employee.emlAddr : '',
    dptUuid: employee && employee.dptUuid !== null ? employee.dptUuid : '',
    gradeUuid:
      employee && employee.gradeUuid !== null ? employee.gradeUuid : '',
    policyUuid: 
      employee && employee.policyUuid !== null ? employee.policyUuid : '',
    telNo: employee && employee.telNo !== null ? employee.telNo : '',
    postAddrTxt:
      employee && employee.postAddrTxt !== null ? employee.postAddrTxt : '',
    postAddrNm:
      employee && employee.postAddrNm !== null ? employee.postAddrNm : '',
    entryDt:
      employee && employee.entryDt
        ? employee.entryDt
        : dayjs().format('YYYY-MM-DD'),
//    gender: employee && employee.gender !== null ? employee.gender : '',
    privilegeList:
      employee && employee.privilegeList !== null ? employee.privilegeList : [],
    useYn: employee ? employee.useYn === 'Y' : true,
    infoDesc: employee && employee.infoDesc !== null ? employee.infoDesc : '',
  };

  const validationSchema = yup.object({
    usrNo: yup.string().required('사번을 입력하세요'),
    usrNm: yup.string().required('이름을 입력하세요'),
    dptUuid: yup.string().required('부서를 선택하세요'),
    gradeUuid: yup.string().required('직급을 선택하세요'),
    emlAddr: yup
      .string('')
      .email('이메일 형식이 아닙니다.')
      .required('이메일을 입력하세요'),
    entryDt: yup.date().nullable(''),
    telNo: yup.string().required('연락처를 입력하세요'),
    postAddrNm: yup.string().nullable('우편번호를 입력하세요'),
    postAddrTxt: yup.string().nullable('주소를 입력하세요'),
    password: yup
      .string()
      .concat(
        type === 'add' ? yup.string().required('비밀번호를 입력하세요') : null,
      )
      .min(6, '비밀번호는 최소 6글자 이상입니다'),
    confirmPassword: yup
      .string()
      .when('password', (password, schema) => {
        if (password || type === 'add')
          return schema.required('비밀번호 확인을 입력하세요');
      })
      .oneOf([yup.ref('password')], '비밀번호와 일치해야 합니다'),
  });

  useEffect(() => {
    if (type === 'edit') {
      delete initialValues.password;
      delete initialValues.confirmPassword;
    }
  }, [type, validationSchema]);

  const formik = useFormik({
    initialValues: initialValues,
    enableReinitialize: true,
    validationSchema: validationSchema,
    onSubmit: async (values, { setSubmitting }) => {
      setSubmitting(true);
      onSave(values);
      formik.resetForm();
      setSubmitting(false);
    },
  });

  const onSave = async (values) => {
    const dataToSubmit = {
      usrNo: values.usrNo.trim(),
      usrNm: values.usrNm.trim(),
      dptUuid: values.dptUuid,
      gradeUuid: values.gradeUuid,
      policyUuid: values.policyUuid,
      emlAddr: values.emlAddr.trim(),
//      gender: values.gender,
      telNo: values.telNo.trim(),
      entryDt: values.entryDt
        ? dayjs(values.entryDt).format('YYYY-MM-DD')
        : dayjs(new Date()).format('YYYY-MM-DD'),
      postAddrNm: values.postAddrNm.trim(),
      postAddrTxt: values.postAddrTxt.trim(),
      privilegeList: values?.privilegeList,
      useYn: values.useYn ? 'Y' : 'N',
      infoDesc: values.infoDesc,
    };

    if (type === 'add') {
      dataToSubmit.passwd = values.password;
      // 관리자만 이 선택칸을 보고 바꿀 수 있다(그 외는 체크된 기본값 그대로 전송) - 서버가
      // 어차피 관리자 체크(C1) 뒤에 이 값을 보므로 비관리자는 이 화면 자체를 못 연다.
      dataToSubmit.role = newEmployeeIsAdmin ? 'ADMIN_ROLE' : 'USER_ROLE';

      try {
        const res = await empApi.addEmployee(dataToSubmit);
        if (res.data) {
          navigate(`/employeemng/${res.data.info.usrUuid}`);
        }
      } catch (err) {
        let message =
          typeof err.response !== 'undefined'
            ? err.response.data.message
            : err.message;
        enqueueSnackbar(message, {
          variant: 'error',
        });
      }
    } else {
      dataToSubmit.usrUuid = employee.usrUuid;
      //console.log(JSON.stringify(dataToSubmit), 'onSave datatosubmit');
      try {
        const res = await empApi.editEmployee(dataToSubmit);
        console.log(res.data.info);
        if (res.data) {
          dispatch(setEmployee(res.data.info));
          enqueueSnackbar('사용자 정보 수정 성공', {
            variant: 'success',
          });
        }
      } catch (err) {
        let message =
          typeof err.response !== 'undefined'
            ? err.response.data.message
            : err.message;
        enqueueSnackbar(message, {
          variant: 'error',
        });
      }
    }
  };

  const onRoleToggleConfirm = async () => {
    setRoleConfirmOpen(false);
    const nextRole = isAdminRole(employee?.role) ? 'USER_ROLE' : 'ADMIN_ROLE';
    try {
      const res = await empApi.changeUsrRole(employee.usrUuid, nextRole);
      if (res.data?.info) {
        dispatch(setEmployee(res.data.info));
      }
      enqueueSnackbar(
        nextRole === 'ADMIN_ROLE' ? '관리자로 변경했습니다.' : '일반 사용자로 변경했습니다.',
        { variant: 'success' },
      );
    } catch (err) {
      let message =
        typeof err.response !== 'undefined' ? err.response.data.message : err.message;
      enqueueSnackbar(message, { variant: 'error' });
    }
  };

  return (
    <>
      {/* 신규 등록: 관리자만 보이고, 기본값은 지금 동작과 같은 "관리자"(오너 결정, 2026-10-27).
          부서별 메뉴권한이 갖춰진 고객사부터 체크를 꺼서 "일반"으로 등록할 수 있다. */}
      {type === 'add' && isAdmin && (
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="subtitle2">관리자 권한</Typography>
            <FormControlLabel
              labelPlacement="start"
              label={newEmployeeIsAdmin ? '관리자' : '일반'}
              control={
                <Switch
                  checked={newEmployeeIsAdmin}
                  onChange={(e) => setNewEmployeeIsAdmin(e.target.checked)}
                />
              }
            />
          </Box>
        </Paper>
      )}
      {/* 수정: 관리자만 보이고, 기존 사원의 역할을 전환한다(마지막 관리자 강등은 서버가 400으로
          막는다). 이 전환은 본문 저장과 별개로 즉시 반영된다(PUT /user/info/{usrUuid}/role). */}
      {type === 'edit' && isAdmin && employee && employee.usrUuid && (
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="subtitle2">관리자 권한</Typography>
            <FormControlLabel
              labelPlacement="start"
              label={isAdminRole(employee.role) ? '관리자' : '일반'}
              control={
                <Switch
                  checked={isAdminRole(employee.role)}
                  onChange={() => setRoleConfirmOpen(true)}
                />
              }
            />
          </Box>
        </Paper>
      )}
      <EmployeeForm formik={formik} employee={employee} />
      <AskDialog
        visible={roleConfirmOpen}
        title="권한 변경"
        description={
          isAdminRole(employee?.role)
            ? employee?.usrUuid === currentUser?.usrUuid
              ? '본인을 일반 사용자로 변경합니다. 변경 즉시 로그아웃되며, 다시 로그인해도 관리자 화면을 쓸 수 없습니다. 계속하시겠습니까?'
              : `${employee?.usrNm ?? ''}님을 일반 사용자로 변경하시겠습니까?`
            : `${employee?.usrNm ?? ''}님을 관리자로 지정하시겠습니까?`
        }
        confirmText="변경"
        onConfirm={onRoleToggleConfirm}
        onCancle={() => setRoleConfirmOpen(false)}
      />
    </>
  );
};

export default EmployeeFormContainer;
